/**
 * Website Configuration Service
 * Manages homepage settings stored in Firestore
 * AI Agents will use this same service to update the website
 */

import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';

/**
 * Upload a website image (gallery / hero) from the owner's device to Firebase Storage
 * and return its public URL. This is the same pattern barber/service photos use — the
 * file lives in YOUR storage bucket, no third-party hosting needed.
 */
export async function uploadWebsiteImage(file: File, folder: 'gallery' | 'hero' | 'logo'): Promise<string> {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `website/${folder}/${Date.now()}_${safeName}`;
  const r = storageRef(storage, path);
  await uploadBytes(r, file);
  return getDownloadURL(r);
}

export interface HeroConfig {
  headline: string;
  subtext: string;
  imageUrl: string;
  ctaText: string;
  ctaLink: string;
}

export interface ShopInfo {
  name: string;
  address: string;
  phone: string;
  hours: string;
  description: string;
}

export interface SocialLinks {
  instagram: string;
  facebook: string;
  twitter: string;
}

export interface SEOConfig {
  title: string;
  description: string;
  keywords: string[];
}

export interface Testimonial {
  id: string;
  name: string;
  rating: number;
  text: string;
  date: string;
}

export interface SiteStats {
  happyClients: string;    // e.g. "500+"
  haircutsDone: string;    // e.g. "10K+"
  yearsExperience: string; // e.g. "5+"
  rating: string;          // e.g. "4.9"
  reviewCount: string;     // e.g. "120+"
}

export interface WebsiteConfig {
  logo?: string;          // shop logo (Storage URL) — flows to every customer surface via <ShopLogo>
  tagline?: string;       // shown under the shop name on every customer surface (default "Grooming for Every Man")
  shopInfo: ShopInfo;
  hero: HeroConfig;
  featuredServiceIds: string[];
  featuredBarberIds: string[];
  testimonials: Testimonial[];
  galleryImages: string[];
  socialLinks: SocialLinks;
  seo: SEOConfig;
  stats: SiteStats;
  updatedAt: Date;
  updatedBy: string;
}

// Default configuration
const DEFAULT_CONFIG: WebsiteConfig = {
  shopInfo: {
    name: 'SLIK Barbershop',
    address: '123 Main Street, Downtown',
    phone: '(555) 123-4567',
    hours: 'Mon-Sat: 9AM - 8PM',
    description: 'Premium barbershop experience with skilled barbers, modern atmosphere, and exceptional service.',
  },
  hero: {
    headline: 'Premium Cuts. Exceptional Style.',
    subtext: 'Experience the best in grooming with our team of skilled professionals.',
    imageUrl: 'https://images.unsplash.com/photo-1585747860715-2ba37e788b70?w=1920&q=80',
    ctaText: 'Book Now',
    ctaLink: '/book',
  },
  featuredServiceIds: [],
  featuredBarberIds: [],
  testimonials: [
    {
      id: '1',
      name: 'Marcus J.',
      rating: 5,
      text: 'Best barbershop in town! The attention to detail is incredible.',
      date: '2 weeks ago',
    },
    {
      id: '2',
      name: 'David R.',
      rating: 5,
      text: 'Finally found my go-to spot. Clean cuts every time.',
      date: '1 month ago',
    },
    {
      id: '3',
      name: 'Chris T.',
      rating: 5,
      text: 'Professional service and great atmosphere. Highly recommend!',
      date: '3 weeks ago',
    },
  ],
  galleryImages: [
    'https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=600&q=80',
    'https://images.unsplash.com/photo-1621605815971-fbc98d665033?w=600&q=80',
    'https://images.unsplash.com/photo-1622286342621-4bd786c2447c?w=600&q=80',
  ],
  socialLinks: {
    instagram: '',
    facebook: '',
    twitter: '',
  },
  seo: {
    title: 'SLIK Barbershop | Premium Cuts & Grooming',
    description: 'Book your appointment at SLIK Barbershop. Professional haircuts, beard trims, and grooming services.',
    keywords: ['barbershop', 'haircut', 'grooming', 'beard trim'],
  },
  stats: {
    happyClients: '500+',
    haircutsDone: '10K+',
    yearsExperience: '5+',
    rating: '4.9',
    reviewCount: '120+',
  },
  updatedAt: new Date(),
  updatedBy: 'system',
};

const CONFIG_DOC_ID = 'main'; // Single config document for now

class WebsiteConfigService {
  private configRef = doc(db, 'website_config', CONFIG_DOC_ID);
  private cachedConfig: WebsiteConfig | null = null;
  private listeners: ((config: WebsiteConfig) => void)[] = [];

  /**
   * Get the current website configuration
   */
  async getConfig(): Promise<WebsiteConfig> {
    try {
      const docSnap = await getDoc(this.configRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        this.cachedConfig = {
          ...DEFAULT_CONFIG,
          ...data,
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as WebsiteConfig;
      } else {
        // Initialize with defaults if doesn't exist
        await this.saveConfig(DEFAULT_CONFIG);
        this.cachedConfig = DEFAULT_CONFIG;
      }

      return this.cachedConfig;
    } catch (error) {
      console.error('Error fetching website config:', error);
      return DEFAULT_CONFIG;
    }
  }

  /**
   * Save the website configuration
   */
  async saveConfig(config: Partial<WebsiteConfig>, updatedBy: string = 'admin'): Promise<boolean> {
    try {
      const fullConfig: WebsiteConfig = {
        ...DEFAULT_CONFIG,
        ...this.cachedConfig,
        ...config,
        updatedAt: new Date(),
        updatedBy,
      };

      await setDoc(this.configRef, {
        ...fullConfig,
        updatedAt: fullConfig.updatedAt,
      });

      this.cachedConfig = fullConfig;
      this.notifyListeners(fullConfig);

      return true;
    } catch (error) {
      console.error('Error saving website config:', error);
      return false;
    }
  }

  /**
   * Update specific section of the config
   */
  async updateSection<K extends keyof WebsiteConfig>(
    section: K,
    value: WebsiteConfig[K],
    updatedBy: string = 'admin'
  ): Promise<boolean> {
    return this.saveConfig({ [section]: value } as Partial<WebsiteConfig>, updatedBy);
  }

  /**
   * Subscribe to config changes (real-time updates)
   */
  subscribe(callback: (config: WebsiteConfig) => void): () => void {
    this.listeners.push(callback);

    // Set up Firestore real-time listener
    const unsubscribe = onSnapshot(this.configRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        this.cachedConfig = {
          ...DEFAULT_CONFIG,
          ...data,
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as WebsiteConfig;
        this.notifyListeners(this.cachedConfig);
      }
    });

    // Return cleanup function
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
      unsubscribe();
    };
  }

  /**
   * Get cached config (for quick access)
   */
  getCached(): WebsiteConfig | null {
    return this.cachedConfig;
  }

  /**
   * Reset to default configuration
   */
  async resetToDefaults(updatedBy: string = 'admin'): Promise<boolean> {
    return this.saveConfig(DEFAULT_CONFIG, updatedBy);
  }

  private notifyListeners(config: WebsiteConfig) {
    this.listeners.forEach(callback => callback(config));
  }
}

// Export singleton instance
export const websiteConfigService = new WebsiteConfigService();

// Export default config for reference
export { DEFAULT_CONFIG };
